import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { Upload, Save, Image as ImageIcon } from 'lucide-react';

const SiteSettingsManager = () => {
  const [settings, setSettings] = useState({
    site_title: '',
    site_description: '',
    copyright_text: '',
    contact_page_title: '',
    contact_page_description: '',
    logo_url: null,
    footer_logo_url: null,
    favicon_url: null,
    cert_photo_url: null,
    boost_photo_url: null,
    home_banner_url: null
  });
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/site-settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTextUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      const formData = new URLSearchParams();
      formData.append('site_title', settings.site_title);
      formData.append('site_description', settings.site_description);
      formData.append('copyright_text', settings.copyright_text);
      formData.append('contact_page_title', settings.contact_page_title);
      formData.append('contact_page_description', settings.contact_page_description);

      const response = await fetch(`${API_BASE}/api/admin/site-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Site settings updated successfully'
        });
        fetchSettings();
      } else {
        throw new Error('Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to update settings',
        variant: 'destructive'
      });
    }
  };

  const handleImageUpload = async (imageType, file) => {
    if (!file) return;

    try {
      setUploading(prev => ({ ...prev, [imageType]: true }));
      const token = localStorage.getItem('token');
      
      // Upload image first
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await fetch(`${API_BASE}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image');
      }

      const uploadData = await uploadResponse.json();
      const imageUrl = uploadData.url;

      // Update settings with new image URL
      const updateResponse = await fetch(`${API_BASE}/api/admin/site-settings/images?image_type=${imageType}&image_url=${encodeURIComponent(imageUrl)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (updateResponse.ok) {
        toast({
          title: 'Success',
          description: `${imageType} updated successfully`
        });
        fetchSettings();
      } else {
        throw new Error('Failed to update image setting');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Error',
        description: `Failed to upload ${imageType}`,
        variant: 'destructive'
      });
    } finally {
      setUploading(prev => ({ ...prev, [imageType]: false }));
    }
  };

  const ImageUploadField = ({ label, imageType, dimensions, currentUrl }) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        {label} <span className="text-gray-500 dark:text-gray-400 text-xs">*{dimensions}</span>
      </Label>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => e.target.files[0] && handleImageUpload(imageType, e.target.files[0])}
            className="flex-1"
            disabled={uploading[imageType]}
          />
          {uploading[imageType] && (
            <span className="text-sm text-gray-500 dark:text-gray-400">Uploading...</span>
          )}
        </div>
        {currentUrl && (
          <div className="flex items-center gap-2 p-2 bg-green-50 border border dark:border-gray-700-green-200 rounded">
            <ImageIcon className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700">Current: {currentUrl}</span>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-gray-500 dark:text-gray-400">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Site Settings</h1>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>General Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Site Title */}
          <div className="space-y-2">
            <Label>Site Title <span className="text-gray-500 dark:text-gray-400 text-xs">(Max 60 Characters)</span></Label>
            <Input
              value={settings.site_title}
              onChange={(e) => setSettings({ ...settings, site_title: e.target.value.slice(0, 60) })}
              placeholder="Trainer List - One Goal Passion!"
              maxLength={60}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">{settings.site_title.length}/60 characters</p>
          </div>

          {/* Site Description */}
          <div className="space-y-2">
            <Label>Site Description <span className="text-gray-500 dark:text-gray-400 text-xs">(Max 160 Characters)</span></Label>
            <Input
              value={settings.site_description}
              onChange={(e) => setSettings({ ...settings, site_description: e.target.value.slice(0, 160) })}
              placeholder="Find your perfect personal trainer"
              maxLength={160}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">{settings.site_description.length}/160 characters</p>
          </div>

          {/* Copyright */}
          <div className="space-y-2">
            <Label>Copyright (Footer)</Label>
            <Input
              value={settings.copyright_text}
              onChange={(e) => setSettings({ ...settings, copyright_text: e.target.value })}
              placeholder="© 2025 TrainerList Inc. All Right Reserved!"
            />
          </div>

          {/* Contact Page Title */}
          <div className="space-y-2">
            <Label>Contact Page Title <span className="text-gray-500 dark:text-gray-400 text-xs">(Max 60 Characters)</span></Label>
            <Input
              value={settings.contact_page_title}
              onChange={(e) => setSettings({ ...settings, contact_page_title: e.target.value.slice(0, 60) })}
              placeholder="Contact Page"
              maxLength={60}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">{settings.contact_page_title.length}/60 characters</p>
          </div>

          {/* Contact Page Description */}
          <div className="space-y-2">
            <Label>Contact Page Description <span className="text-gray-500 dark:text-gray-400 text-xs">(Max 160 Characters)</span></Label>
            <Input
              value={settings.contact_page_description}
              onChange={(e) => setSettings({ ...settings, contact_page_description: e.target.value.slice(0, 160) })}
              placeholder="Get in touch with us"
              maxLength={160}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">{settings.contact_page_description.length}/160 characters</p>
          </div>

          <Button onClick={handleTextUpdate} className="w-full">
            <Save className="w-4 h-4 mr-2" />
            Save Text Settings
          </Button>
        </CardContent>
      </Card>

      {/* Image Uploads */}
      <Card>
        <CardHeader>
          <CardTitle>Image Assets</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ImageUploadField
              label="Logo"
              imageType="logo"
              dimensions="292×32 (.png)"
              currentUrl={settings.logo_url}
            />
            <ImageUploadField
              label="Footer Logo"
              imageType="footer_logo"
              dimensions="300×100 (.png)"
              currentUrl={settings.footer_logo_url}
            />
            <ImageUploadField
              label="Favicon"
              imageType="favicon"
              dimensions="64×64 (.ico)"
              currentUrl={settings.favicon_url}
            />
            <ImageUploadField
              label="Cert Photo"
              imageType="cert_photo"
              dimensions="64×64 (.png)"
              currentUrl={settings.cert_photo_url}
            />
            <ImageUploadField
              label="Boost Photo"
              imageType="boost_photo"
              dimensions="87×87 (.png)"
              currentUrl={settings.boost_photo_url}
            />
            <ImageUploadField
              label="Home Banner"
              imageType="home_banner"
              dimensions="(.png)"
              currentUrl={settings.home_banner_url}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SiteSettingsManager;
